import React, { useEffect, useState } from 'react';
import { logout } from '../api';
import type { PageKey, User, Workspace } from '../types';
import {
  DashboardIcon,
  AssistantIcon,
  StrategyIcon,
  ContentIcon,
  CalendarIcon,
  ScheduleIcon,
  MediaIcon,
  SettingsIcon,
  SupervisorIcon,
  EngagementIcon,
  TasksIcon,
  LogoutIcon,
} from './NavIcons';
import { Logo } from './ConistraMark';
import './Sidebar.css';

type NavItemDef = {
  id: PageKey;
  label: string;
  Icon: React.ComponentType<{ size?: number }>;
};

type NavSection = {
  id: string;
  label: string | null;
  items: NavItemDef[];
};

const navSections: NavSection[] = [
  {
    id: 'overview',
    label: null,
    items: [
      { id: 'dashboard', label: 'Dashboard', Icon: DashboardIcon },
      { id: 'tasks', label: 'Needs You', Icon: TasksIcon },
    ],
  },
  {
    id: 'ai',
    label: 'AI',
    items: [{ id: 'assistant', label: 'AI Assistant', Icon: AssistantIcon }],
  },
  {
    id: 'strategy',
    label: 'Strategy',
    items: [{ id: 'strategy', label: 'Strategy', Icon: StrategyIcon }],
  },
  {
    id: 'content',
    label: 'Content',
    items: [
      { id: 'content', label: 'Outlines & Posts', Icon: ContentIcon },
      { id: 'calendar', label: 'Calendar', Icon: CalendarIcon },
      { id: 'media', label: 'Media', Icon: MediaIcon },
    ],
  },
  {
    id: 'publish',
    label: 'Publish',
    items: [{ id: 'schedule', label: 'Schedule', Icon: ScheduleIcon }],
  },
  {
    id: 'agents',
    label: 'Agents',
    items: [
      { id: 'supervisor', label: 'Supervisor', Icon: SupervisorIcon },
      { id: 'engagement', label: 'Engagement', Icon: EngagementIcon },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    items: [{ id: 'settings', label: 'Settings', Icon: SettingsIcon }],
  },
];

type SidebarProps = {
  currentPage: PageKey;
  onNavigate: (_page: PageKey) => void;
  user: User | null;
  workspace: Workspace | null;
  isNavigationLocked?: boolean;
  onLogout: () => void;
  isOpen: boolean;
  onClose: () => void;
  tasksOpenCount?: number;
};

function Sidebar({
  currentPage,
  onNavigate,
  user,
  workspace,
  isNavigationLocked = false,
  onLogout,
  isOpen,
  onClose,
  tasksOpenCount = 0,
}: SidebarProps) {
  const [unlockPhrase, setUnlockPhrase] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);

  useEffect(() => {
    if (isNavigationLocked) {
      setIsUnlocked(false);
      setUnlockPhrase('');
    }
  }, [isNavigationLocked]);

  const handleLogout = async () => {
    try {
      await logout();
      onLogout();
    } catch (err) {
      console.error('Logout failed:', err);
      onLogout();
    }
  };

  const handleNavClick = (pageId: PageKey) => {
    if (isNavigationLocked && !isUnlocked) return;
    onNavigate(pageId);
    onClose();
  };

  const navLocked = isNavigationLocked && !isUnlocked;

  const handleUnlockSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (unlockPhrase.trim().toLowerCase() === 'unlock') {
      setIsUnlocked(true);
      setUnlockPhrase('');
    }
  };

  const name = user?.username ?? '';
  const userInitials =
    name.length >= 2
      ? name.slice(0, 2).toUpperCase()
      : name.length === 1
        ? name.toUpperCase()
        : '?';

  // Autopilot = the supervisor agent running the account on its own.
  const autopilotOn = Boolean(workspace?.supervisor_enabled);
  const hasAutopilot = workspace?.has_autopilot !== false;
  const autopilotText = autopilotOn
    ? "Publishing on its own. You'll only hear from me when it matters."
    : hasAutopilot
      ? 'Paused. Turn it on to let me publish for you.'
      : 'Let Conistra run your account for you on Pro.';
  const autopilotPill = autopilotOn ? 'On' : hasAutopilot ? 'Paused' : 'Pro';

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`sidebar-overlay${isOpen ? ' sidebar-overlay--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={`sidebar${isOpen ? ' sidebar--open' : ''}${navLocked ? ' sidebar--locked' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="brand-logo">
            <Logo size={26} />
          </div>
          {workspace && (
            <div className="brand-workspace">
              <span className="workspace-dot" aria-hidden="true" />
              <span className="workspace-name" title={workspace.name}>
                {workspace.name}
              </span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav" aria-label="Main menu">
          {navLocked && (
            <form className="sidebar-lockbox" onSubmit={handleUnlockSubmit}>
              <p className="sidebar-lockbox__title">Activation running</p>
              <p className="sidebar-lockbox__hint">Type unlock if you want to leave this flow.</p>
              <label className="sr-only" htmlFor="sidebar-unlock-input">
                Unlock navigation
              </label>
              <input
                id="sidebar-unlock-input"
                className="sidebar-lockbox__input"
                type="text"
                value={unlockPhrase}
                onChange={(event) => setUnlockPhrase(event.target.value)}
                placeholder="Type unlock"
                autoComplete="off"
              />
              <button
                type="submit"
                className="sidebar-lockbox__button"
                disabled={unlockPhrase.trim().toLowerCase() !== 'unlock'}
              >
                Unlock
              </button>
            </form>
          )}

          {navSections.map((section) => (
            <div key={section.id} className="nav-section">
              {section.label && (
                <div className="nav-section-label" aria-hidden="true">
                  {section.label}
                </div>
              )}
              {section.items.map((item) => {
                const active = currentPage === item.id;
                return (
                  <button
                    key={item.id}
                    className={`nav-item${active ? ' nav-item--active' : ''}`}
                    onClick={() => handleNavClick(item.id)}
                    disabled={navLocked}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="nav-item-icon">
                      <item.Icon size={16} />
                    </span>
                    <span className="nav-item-label">{item.label}</span>
                    {item.id === 'tasks' && tasksOpenCount > 0 && (
                      <span
                        className="nav-item-badge"
                        aria-label={`${tasksOpenCount} open tasks`}
                      >
                        {tasksOpenCount > 9 ? '9+' : tasksOpenCount}
                      </span>
                    )}
                    {active && <span className="nav-item-indicator" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Autopilot status card */}
        {workspace && (
          <button
            type="button"
            className="sidebar-autopilot"
            onClick={() => handleNavClick('supervisor')}
            disabled={navLocked}
            aria-label="Manage autopilot"
          >
            <span className={`sidebar-autopilot__pill sidebar-autopilot__pill--${autopilotOn ? 'on' : 'off'}`}>
              <span className="sidebar-autopilot__dot" aria-hidden="true" />
              {autopilotPill}
            </span>
            <span className="sidebar-autopilot__label">AUTOPILOT</span>
            <span className="sidebar-autopilot__text">{autopilotText}</span>
          </button>
        )}

        {/* Footer / user account */}
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar" aria-hidden="true">
              {userInitials}
            </div>
            <div className="user-info">
              <div className="user-name">{user?.username}</div>
              {user?.email && <div className="user-email">{user.email}</div>}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="logout-btn"
            title="Log out"
            aria-label="Log out"
          >
            <LogoutIcon size={16} />
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
